import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Factory, RefreshCw, Search } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

// Admin-only: map a product to a Vitatides CompoundOS formula + fill size and
// send / revise / cancel replenishment requests. Every open request lands on
// the live CompoundOS production plan, so nothing here sends without a click.

interface Mapping {
  formulaId: string;
  formulaName?: string;
  fillVolume: number;
  fillVolumeUnit: string;
}

interface ProductionRequest {
  _id: string;
  requestId: string;
  quantity: number;
  neededBy: string;
  reason?: string;
  state: 'pending_send' | 'send_failed' | 'open' | 'fulfilled' | 'cancelled';
  fulfilledQuantity: number;
  allocatedQuantity: number;
  outstandingQuantity: number;
  lastError?: string;
  lastSyncedAt?: string;
  createdAt: string;
}

interface Suggestion {
  windowStart: string;
  windowEnd: string;
  unitsInWindow: number;
  dailyRate: number;
  coverDays: number;
  runRateUnits: number;
  refillUnits: number;
  suggestedQuantity: number;
}

interface Summary {
  configured: boolean;
  product: { outOfStock?: boolean; outOfStockSince?: string; compoundOs?: Mapping };
  suggestion: Suggestion;
  requests: ProductionRequest[];
}

const STATE_STYLE: Record<ProductionRequest['state'], string> = {
  pending_send: 'bg-amber-100 text-amber-800',
  send_failed: 'bg-red-100 text-red-800',
  open: 'bg-blue-100 text-blue-800',
  fulfilled: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600'
};
const STATE_LABEL: Record<ProductionRequest['state'], string> = {
  pending_send: 'Sending…',
  send_failed: 'Rejected',
  open: 'Open',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled'
};

function ymdFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const base = '/admin/compoundos';

export function CompoundOsReplenishmentPanel({ productId }: { productId: string }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingMapping, setEditingMapping] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<{ success: boolean; data: Summary; error?: string }>(`${base}/products/${productId}`);
      if (res.success) {
        setSummary(res.data);
        setLoadError(null);
      } else {
        setLoadError(res.error || 'Could not load CompoundOS status.');
      }
    } catch (err: any) {
      setLoadError(err?.response?.data?.error || 'Could not load CompoundOS status.');
    }
  }, [productId]);

  useEffect(() => { void load(); }, [load]);

  if (loadError) return <p className="text-xs text-red-600">{loadError}</p>;
  if (!summary) return null;

  const mapping = summary.product.compoundOs;
  const live = summary.requests.find(r => r.state === 'open' || r.state === 'pending_send');

  return (
    <div className="mt-2 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
      <div className="flex items-center gap-2 font-medium text-gray-800">
        <Factory className="h-4 w-4 text-gray-500" /> Replenishment · CompoundOS (Vitatides)
      </div>

      {!summary.configured && (
        <p className="text-xs text-gray-500">CompoundOS isn’t configured for this tenant (no API key), so requests can’t be sent yet.</p>
      )}

      {summary.product.outOfStock && !live && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Out of stock{summary.product.outOfStockSince ? ` since ${new Date(summary.product.outOfStockSince).toLocaleDateString()}` : ''} with no replenishment request open.
        </div>
      )}

      {/* Mapping */}
      {editingMapping || !mapping ? (
        <MappingEditor
          productId={productId}
          current={mapping}
          disabled={!summary.configured}
          locked={!!live}
          onCancel={mapping ? () => setEditingMapping(false) : undefined}
          onSaved={() => { setEditingMapping(false); void load(); }}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-gray-700">
            <span className="font-medium">{mapping.formulaName || mapping.formulaId}</span>
            <span className="text-gray-500"> · {mapping.fillVolume} {mapping.fillVolumeUnit} · formula {mapping.formulaId}</span>
          </span>
          <button
            onClick={() => setEditingMapping(true)}
            disabled={!!live}
            title={live ? 'Cancel the open request before changing the mapping' : undefined}
            className="text-xs text-blue-700 underline disabled:cursor-not-allowed disabled:text-gray-400"
          >
            Change mapping
          </button>
        </div>
      )}

      {/* Request */}
      {mapping && summary.configured && (
        live
          ? <LiveRequest request={live} onChanged={load} />
          : <NewRequestForm productId={productId} suggestion={summary.suggestion} onCreated={load} />
      )}

      {summary.requests.filter(r => r !== live).length > 0 && (
        <details className="text-xs text-gray-600">
          <summary className="cursor-pointer">Earlier requests</summary>
          <ul className="mt-1 space-y-1">
            {summary.requests.filter(r => r !== live).map(r => (
              <li key={r._id} className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 ${STATE_STYLE[r.state]}`}>{STATE_LABEL[r.state]}</span>
                {r.quantity} by {r.neededBy} · {r.fulfilledQuantity} received
                {r.lastError && r.state === 'send_failed' && <span className="text-red-600">· {r.lastError}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function MappingEditor({
  productId, current, disabled, locked, onCancel, onSaved
}: {
  productId: string;
  current?: Mapping;
  disabled: boolean;
  locked: boolean;
  onCancel?: () => void;
  onSaved: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<Record<string, any>>>([]);
  const [formulaId, setFormulaId] = useState(current?.formulaId || '');
  const [fillVolume, setFillVolume] = useState(current ? String(current.fillVolume) : '');
  const [fillVolumeUnit, setFillVolumeUnit] = useState(current?.fillVolumeUnit || 'mL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async () => {
    if (search.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.get<any>(`${base}/formulary?search=${encodeURIComponent(search.trim())}`);
      setResults(res.success ? res.data.data || [] : []);
      if (!res.success) setError(res.error || 'Search failed.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Search failed.');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.put<any>(`${base}/products/${productId}/mapping`, {
        formulaId, fillVolume: Number(fillVolume), fillVolumeUnit
      });
      if (res.success) {
        toast.success('CompoundOS mapping saved.');
        onSaved();
      } else {
        setError(res.error || 'Could not save the mapping.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the mapping.');
    } finally {
      setBusy(false);
    }
  };

  if (disabled) return <p className="text-xs text-gray-500">Not mapped to a CompoundOS formula.</p>;
  if (locked) return <p className="text-xs text-gray-500">Cancel the open request before changing the mapping.</p>;

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Pick the Vitatides formula and the exact active fill size this product is made as. Confirm the size with the
        CompoundOS team — the formulary’s volume list isn’t authoritative.
      </p>
      <div className="flex gap-2">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void runSearch(); } }}
          placeholder="Search formulary, e.g. Semaglutide"
          className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
        />
        <button onClick={runSearch} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-100">
          <Search className="h-3.5 w-3.5" /> Search
        </button>
      </div>
      {results.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white text-xs">
          {results.map(f => (
            <li key={String(f.id)}>
              <button
                onClick={() => setFormulaId(String(f.id))}
                className={`w-full px-2 py-1 text-left hover:bg-blue-50 ${formulaId === String(f.id) ? 'bg-blue-50 font-medium' : ''}`}
              >
                {String(f.name || f.id)}
                <span className="text-gray-400"> · {String(f.id)}</span>
                {Array.isArray(f.availableVolumes) && f.availableVolumes.length > 0 && (
                  <span className="text-gray-400">
                    {' '}· labels: {f.availableVolumes.map((v: any) => (typeof v === 'object' ? `${v.volume} ${v.unit}` : String(v))).join(', ')}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input value={formulaId} onChange={e => setFormulaId(e.target.value)} placeholder="Formula ID" className="w-44 rounded-md border border-gray-300 px-2 py-1 text-sm" />
        <input value={fillVolume} onChange={e => setFillVolume(e.target.value)} placeholder="Fill size" inputMode="decimal" className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm" />
        <select value={fillVolumeUnit} onChange={e => setFillVolumeUnit(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
          <option value="mL">mL</option>
          <option value="g">g</option>
          <option value="tablet">tablet</option>
        </select>
        <button
          onClick={save}
          disabled={busy || !formulaId.trim() || !(Number(fillVolume) > 0)}
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Save mapping
        </button>
        {onCancel && <button onClick={onCancel} className="text-xs text-gray-600 underline">Cancel</button>}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function NewRequestForm({ productId, suggestion, onCreated }: { productId: string; suggestion: Suggestion; onCreated: () => void }) {
  const [quantity, setQuantity] = useState(String(suggestion.suggestedQuantity || ''));
  const [neededBy, setNeededBy] = useState(ymdFromToday(14));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post<any>(`${base}/products/${productId}/requests`, {
        quantity: Number(quantity), neededBy, reason: reason.trim() || undefined
      });
      if (res.success) {
        toast.success(res.message || 'Replenishment requested.');
        onCreated();
      } else {
        setError(res.error || 'The request was not accepted.');
        onCreated();
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The request was not accepted.');
      onCreated();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Suggested <strong>{suggestion.suggestedQuantity}</strong>: {suggestion.unitsInWindow} sold {suggestion.windowStart}–{suggestion.windowEnd}
        {' '}({suggestion.dailyRate}/day × {suggestion.coverDays} days = {suggestion.runRateUnits})
        {suggestion.refillUnits > 0 ? ` + ${suggestion.refillUnits} in scheduled refills` : ''}. Units are vials (or g / tablets for bulk).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-gray-600">Quantity
          <input value={quantity} onChange={e => setQuantity(e.target.value)} inputMode="numeric" className="ml-1 w-24 rounded-md border border-gray-300 px-2 py-1 text-sm" />
        </label>
        <label className="text-xs text-gray-600">Needed by
          <input type="date" value={neededBy} onChange={e => setNeededBy(e.target.value)} className="ml-1 rounded-md border border-gray-300 px-2 py-1 text-sm" />
        </label>
      </div>
      <input
        value={reason}
        onChange={e => setReason(e.target.value)}
        maxLength={1000}
        placeholder="Reason (no patient information), e.g. Out of stock; refills and steady demand"
        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        onClick={submit}
        disabled={busy || !(Number.isInteger(Number(quantity)) && Number(quantity) >= 1) || !neededBy}
        className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {busy ? 'Sending…' : 'Request from Vitatides'}
      </button>
    </div>
  );
}

function LiveRequest({ request, onChanged }: { request: ProductionRequest; onChanged: () => void }) {
  const [revising, setRevising] = useState(false);
  const [quantity, setQuantity] = useState(String(request.quantity));
  const [neededBy, setNeededBy] = useState(request.neededBy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.success) {
        toast.success(res.message || ok);
        setRevising(false);
      } else {
        setError(res.error || 'CompoundOS did not accept that.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'CompoundOS did not accept that.');
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-white p-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 ${STATE_STYLE[request.state]}`}>{STATE_LABEL[request.state]}</span>
        <span className="text-gray-800">
          {request.quantity} by {request.neededBy} · {request.fulfilledQuantity} received · {request.outstandingQuantity} outstanding
          {request.allocatedQuantity > 0 ? ` · ${request.allocatedQuantity} allocated` : ''}
        </span>
        <button
          onClick={() => act(() => apiClient.post(`${base}/requests/${request._id}/sync`), 'Refreshed.')}
          disabled={busy}
          title="Refresh from CompoundOS"
          className="text-gray-500 hover:text-gray-700"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className="text-xs text-gray-400">{request.requestId}</p>
      {request.lastError && <p className="text-xs text-amber-700">Last error: {request.lastError}</p>}

      {request.state === 'open' && (
        revising ? (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-600">New total
              <input value={quantity} onChange={e => setQuantity(e.target.value)} inputMode="numeric" className="ml-1 w-24 rounded-md border border-gray-300 px-2 py-1 text-sm" />
            </label>
            <input type="date" value={neededBy} onChange={e => setNeededBy(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-sm" />
            <button
              onClick={() => act(() => apiClient.put(`${base}/requests/${request._id}`, { quantity: Number(quantity), neededBy }), 'Revised.')}
              disabled={busy}
              className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              Save
            </button>
            <button onClick={() => setRevising(false)} className="text-xs text-gray-600 underline">Cancel</button>
            <p className="w-full text-xs text-gray-500">
              This is the new TOTAL, including the {request.fulfilledQuantity} already received.
            </p>
          </div>
        ) : (
          <div className="flex gap-3">
            <button onClick={() => setRevising(true)} className="text-xs text-blue-700 underline">Revise</button>
            <button
              onClick={() => act(() => apiClient.post(`${base}/requests/${request._id}/cancel`), 'Cancelled.')}
              disabled={busy}
              className="text-xs text-red-700 underline"
            >
              Cancel outstanding
            </button>
          </div>
        )
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
